import Box from "@material-ui/core/Box";
import React, { useEffect, useState } from "react";
import clsx from "clsx";
import isAfter from "date-fns/isAfter";
import isBefore from "date-fns/isBefore";
import isSameDay from "date-fns/isSameDay";
import isSameMonth from "date-fns/isSameMonth";
import startOfToday from "date-fns/startOfToday";
import startOfWeek from "date-fns/startOfWeek";
import startOfMonth from "date-fns/startOfMonth";
import addMonths from "date-fns/addMonths";
import format from "date-fns/format";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import ChevronRight from "@material-ui/icons/ChevronRight";
import ChevronLeft from "@material-ui/icons/ChevronLeft";

import CalendarOutline from "components/Icons/CalendarOutline";
import { getTypedDateRange, isBetweenDates } from "utils/date-utils";
import { IDateRange } from "utils/facilityBlackouts";
import useStyles from "./styles";

enum DatePreview {
  FROM,
  TO
}

interface Props {
  defaultRange: IDateRange;
  onDateRangeChange: (range: IDateRange) => void;
  showCalendar?: boolean;
  previewFormat?: string;
  disablePast?: boolean;
  onOpen?: () => void;
  classes?: Record<string, any>;
}

interface ICalendarDate {
  date: Date;
  disabled?: boolean;
}
const dateToCalendar = (date: Date): ICalendarDate => {
  return {
    date,
    disabled: isBefore(date, startOfToday())
  };
};

const DateRangeCalendar = (props: Props) => {
  const {
    onDateRangeChange,
    onOpen,
    defaultRange,
    showCalendar,
    disablePast,
    classes = {},
    previewFormat = "iiii, MMM. d"
  } = props;
  const ownClasses = useStyles();
  const today = startOfToday();
  const [activeMonth, setActiveMonth] = useState<Date>(
    defaultRange.startDate || today
  );
  const [startDate, setStartDate] = useState<Date>(defaultRange.startDate);
  const [endDate, setEndDate] = useState<Date>(defaultRange.endDate);
  const [activeDatePreview, setActiveDatePreview] = useState<DatePreview>(
    DatePreview.FROM
  );
  const [isDateRangeOpen, setIsDateRangeOpen] = useState<boolean>(showCalendar);
  const calendarStartDate = startOfWeek(startOfMonth(activeMonth));

  const calendar = getTypedDateRange<ICalendarDate>(
    calendarStartDate,
    35,
    dateToCalendar
  );

  const dayNames = getTypedDateRange<string>(
    calendarStartDate,
    7,
    (date: Date) => format(date, "iii")
  );

  const isOneDayRange = isSameDay(startDate, endDate);

  const handleMonthChange = offset => () => {
    setActiveMonth(addMonths(activeMonth, offset));
  };

  const handleDateChange = date => {
    let start = activeDatePreview === DatePreview.FROM ? date : startDate;
    let end = activeDatePreview === DatePreview.TO ? date : endDate;

    if (isAfter(start, end)) {
      [start, end] = [end, start];
    }

    if (start) {
      setStartDate(start);
    } else {
      setActiveDatePreview(DatePreview.FROM);
    }

    if (end) {
      setEndDate(end);
    } else {
      setActiveDatePreview(DatePreview.TO);
    }

    if (start && end) {
      onDateRangeChange({
        startDate: start,
        endDate: end
      });
    }
  };

  const handleDateClick = (day: ICalendarDate) => () => {
    handleDateChange(day.date);
  };

  const handleDatePreviewClick = (activePreview: DatePreview) => () => {
    if (activePreview === activeDatePreview) {
      !showCalendar && setIsDateRangeOpen(false);
      setActiveDatePreview(null);
    } else {
      setActiveDatePreview(activePreview);
      setIsDateRangeOpen(true);
    }
  };

  useEffect(() => {
    isDateRangeOpen && onOpen && onOpen();
  }, [isDateRangeOpen]);

  if (!defaultRange) {
    return <></>;
  }

  return (
    <>
      <Box className={clsx(ownClasses.dates, classes.dates)}>
        <Box
          className={ownClasses.dateBox}
          onClick={handleDatePreviewClick(DatePreview.FROM)}
        >
          <Typography className={ownClasses.dateTitle}>From</Typography>
          <Typography
            className={clsx(ownClasses.date, {
              [ownClasses.activeDatePreview]:
                activeDatePreview === DatePreview.FROM
            })}
          >
            <CalendarOutline className={ownClasses.calendarIcon} />
            {startDate ? format(startDate, previewFormat) : "-"}
          </Typography>
        </Box>
        <Box
          className={ownClasses.dateBox}
          onClick={handleDatePreviewClick(DatePreview.TO)}
        >
          <Typography className={ownClasses.dateTitle}>To</Typography>
          <Typography
            className={clsx(ownClasses.date, {
              [ownClasses.activeDatePreview]:
                activeDatePreview === DatePreview.TO
            })}
          >
            <CalendarOutline className={ownClasses.calendarIcon} />
            {endDate ? format(endDate, previewFormat) : "-"}
          </Typography>
        </Box>
      </Box>
      {isDateRangeOpen && (
        <Box>
          <Box className={clsx(ownClasses.navigation)}>
            <IconButton
              size={"small"}
              onClick={handleMonthChange(-1)}
              disabled={disablePast ? isSameMonth(activeMonth, today) : false}
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              size={"small"}
              onClick={handleMonthChange(1)}
              disabled={
                disablePast
                  ? activeDatePreview === DatePreview.FROM &&
                    isSameMonth(activeMonth, endDate)
                  : false
              }
            >
              <ChevronRight />
            </IconButton>
            <Typography className={ownClasses.navigationTitle}>
              {format(activeMonth, "MMMM yyyy")}
            </Typography>
          </Box>
          <Box className={ownClasses.dayNames}>
            {dayNames.map(dn => (
              <Typography className={ownClasses.dayName} key={dn}>
                {dn}
              </Typography>
            ))}
          </Box>
          <Box
            className={clsx(
              ownClasses.calendar,
              activeDatePreview === DatePreview.FROM
                ? ownClasses.pickingFrom
                : ownClasses.pickingTo
            )}
          >
            {calendar.map(day => {
              const dateNumber = day.date.getDate();
              const isInRange = isBetweenDates(day.date, startDate, endDate);
              const isStart = isSameDay(day.date, startDate);
              const isEnd = isSameDay(day.date, endDate);
              const isEdgeDate = isStart || isEnd;
              return (
                <Box
                  key={day.date.getTime()}
                  className={ownClasses.calendarDate}
                >
                  <div
                    className={clsx(ownClasses.calendarDateInner, {
                      [ownClasses.calendarDateInRange]: isInRange
                    })}
                  >
                    <Button
                      className={clsx(ownClasses.calendarDateButton, {
                        [ownClasses.calendarEdge]: isEdgeDate,
                        [ownClasses.calendarEndDate]: isEnd,
                        [ownClasses.calendarStartDate]: isStart,
                        [ownClasses.calendarOneDayRange]:
                          isEdgeDate && isOneDayRange
                      })}
                      disabled={disablePast ? day.disabled : false}
                      onClick={handleDateClick(day)}
                    >
                      {dateNumber}
                    </Button>
                    {isEdgeDate && !isOneDayRange && (
                      <Box
                        className={clsx({
                          [ownClasses.calendarDateBg]: startDate && endDate,
                          [ownClasses.calendarDateBgLeft]: isEnd && startDate,
                          [ownClasses.calendarDateBgRight]: isStart && endDate
                        })}
                        component={"span"}
                      />
                    )}
                  </div>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </>
  );
};

export default DateRangeCalendar;
